import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { noteSessionEnded } from '../utils/sessionEnded';
import {
  api, setAccessToken, getAccessToken, refreshAccessToken, setActiveWorkspaceId as setWorkspaceHeader,
  getActiveSlot, setActiveSlot, getAccounts, upsertAccount, removeAccountEntry, nextFreeSlot, MAX_ACCOUNT_SLOTS,
} from '../api/client';
import { applyLanguage } from '../i18n';
import { planAllows } from '../utils/planRule';
import { markAllReadLocally } from '../utils/notificationState';
import { showErrorToast } from '../utils/toast';
import { messageFromError } from '../utils/apiError';

// ── Plan helpers ─────────────────────────────────────────────────────────────
// Decision 9 (round 3): one rule for a paid feature, utils/planRule.js (the server's is the same).
// These let ANY trial through to every tier; a trial OF a plan is now that plan.

/** True when the user's plan includes Pro features (or they are in the open trial of no plan yet). */
export function isPro(user) {
  return planAllows(user, 'pro');
}

/** True when the user's plan includes Team Pro features (or they are in the open trial of no plan yet). */
export function isTeam(user) {
  return planAllows(user, 'team');
}

// ✅ CREATE CONTEXT
const CurrentUserContext = createContext(null);

// ✅ HOOK TO USE CONTEXT
export const useCurrentUser = () => {
  const context = useContext(CurrentUserContext);
  if (!context) {
    throw new Error('useCurrentUser must be used within a CurrentUserProvider');
  }
  return context;
};

// ✅ CONTEXT PROVIDER COMPONENT
export const CurrentUserProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notifications, setNotifications] = useState([]);
  // Block J: a failed load is said on the Notifications page, never read as "nothing waiting".
  const [notificationsLoadFailed, setNotificationsLoadFailed] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  // Fresh-eyes 11 (2026-10-12): the two numbers every surface shows, from the server
  // (lib/notificationCounts): { needsAttention, unread, proposals }, or null when not loaded.
  // The bell, the Notifications page and the Dashboard tile read these and never count for themselves.
  const [notificationCounts, setNotificationCounts] = useState(null);
  // Carla, 2026-10-21: "Mark all as read" clears the bell at once. The latest list and numbers, so the
  // optimistic change can be put back if the server refuses; and while the request is out, a poll that
  // started before it must not paint the old unread number back over the cleared one.
  const notificationsRef = useRef([]);
  const notificationCountsRef = useRef(null);
  notificationsRef.current = notifications;
  notificationCountsRef.current = notificationCounts;
  const readAllPending = useRef(0);
  const [authConnectionFailed, setAuthConnectionFailed] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState(null);
  // True once loadWorkspaces has finished, however it ended. The app waits for the workspace before it
  // mounts (App.js WorkspaceReloader), and must not wait for ever when there is none to choose.
  const [workspacesSettled, setWorkspacesSettled] = useState(false);
  const [workspacesLoadError, setWorkspacesLoadError] = useState(false);
  const [subscriptionCheckPending, setSubscriptionCheckPending] = useState(false);
  const workspacesRef = useRef([]);

  // ── Multi-account (Block 194) ───────────────────────────────────────────────
  const [accounts, setAccounts] = useState(() => getAccounts());
  const refreshAccounts = useCallback(() => setAccounts(getAccounts()), []);

  // The multi-account switcher UI is gated OFF by default: until Block 195
  // isolates the service-worker cache, two accounts in one browser share cached
  // GET responses. Single-account users are unaffected (they never hold a 2nd
  // slot). Enable for testing with localStorage aidey_multi_account = '1'.
  const multiAccountEnabled = (() => {
    try { return localStorage.getItem('aidey_multi_account') === '1'; } catch { return false; }
  })();

  // Per-slot "last active workspace" — was a single global key, which would apply
  // account A's workspace to account B. Slot 0 falls back to the legacy global
  // key so existing users keep their remembered workspace.
  const activeWorkspaceKey = useCallback(() => `aidey_active_workspace_${getActiveSlot()}`, []);
  const readSavedWorkspace = useCallback(() => {
    try {
      const slot = getActiveSlot();
      return localStorage.getItem(`aidey_active_workspace_${slot}`)
        || (slot === 0 ? localStorage.getItem('aidey_active_workspace') : null);
    } catch { return null; }
  }, []);

  // Record the currently-active account into the client-side registry whenever we
  // learn who it is (login / session restore), so the switcher can list it.
  const registerActiveAccount = useCallback((user) => {
    if (!user?.id) return;
    upsertAccount({ slot: getActiveSlot(), userId: user.id, label: user.name || user.email || 'Account' });
    setAccounts(getAccounts());
  }, []);

  // ✅ AUTHENTICATION METHODS
  const login = useCallback(async (email, password, trusted = false) => {
    try {
      setLoading(true);
      setError(null);

      const res = await api('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password, trusted }),
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Invalid email or password');
      }

      if (trusted) {
        localStorage.setItem('aidey_trusted_device', 'true');
      } else {
        localStorage.removeItem('aidey_trusted_device');
      }

      setAccessToken(data.accessToken);
      setCurrentUser(data.user);
      registerActiveAccount(data.user);
      applyLanguage(data.user?.language);
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz && tz !== data.user?.timezone) {
          api('/api/users/me/timezone', { method: 'PATCH', body: JSON.stringify({ timezone: tz }) }).catch(() => {});
        }
      } catch (_) {}
      // Carla, 2026-10-11 (the 8 second "Loading…"): notifications are no longer awaited here.
      // The effect on activeWorkspaceId loads them once the workspace is known; fetched first, they
      // held the splash for a request that was thrown away and fetched again.
      await loadWorkspaces();

      // If the user arrived via a notification deep-link while logged out, a
      // returnTo path was parked in sessionStorage. Dispatch aidey:navigate so
      // AppContent routes them there now that auth is confirmed.
      try {
        const returnTo = sessionStorage.getItem('aidey_return_to');
        if (returnTo) {
          sessionStorage.removeItem('aidey_return_to');
          window.dispatchEvent(new CustomEvent('aidey:navigate', { detail: returnTo }));
        }
      } catch {}

      // Carla's item 8 (2026-10-12): a push tapped before sign-in used to open the Aidey chat here
      // (aidey_notif_ctx). Its page is now the returnTo above; the chat is never opened for it.
      try { sessionStorage.removeItem('aidey_notif_ctx'); } catch {}

      return data.user;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api('/api/auth/logout', { method: 'POST' });
    } catch (_) {
      // best-effort
    }
    // Drop the active account from the client-side registry so it doesn't linger
    // as a stale switcher entry. (Single-account: registry empties → clean logout.)
    removeAccountEntry(getActiveSlot());
    setAccounts(getAccounts());
    setAccessToken(null);
    setCurrentUser(null);
    setNotifications([]);
    setUnreadCount(0);
    setNotificationCounts(null);
    setError(null);
    localStorage.removeItem('currentUserId');
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('currentUser');
  }, []);

  const register = useCallback(async (userData) => {
    try {
      setLoading(true);
      setError(null);

      const res = await api('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify(userData),
      });
      const data = await res.json();

      // Verify-email-first: registration no longer logs the user in. The server
      // returns a neutral { ok: true } for both new and existing emails; the user
      // activates via the emailed link, then signs in. No token to store here.
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ USER PROFILE METHODS
  const updateProfile = useCallback(async (updates) => {
    if (!currentUser) return;

    try {
      const res = await api('/api/users/me', {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to update profile');
      }

      setCurrentUser(data.user);
      return data.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [currentUser]);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    if (!currentUser) return;

    try {
      const res = await api('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to change password');
      }
      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [currentUser]);

  // ✅ NOTIFICATION METHODS
  const loadNotifications = useCallback(async () => {
    if (!getAccessToken()) return; // no token, skip, avoids 401 before session is restored
    try {
      const startedDuringReadAll = readAllPending.current > 0;
      const res = await api('/api/notifications');
      const data = await res.json();
      // Answered while a "Mark all as read" was out (or begun before it): its unread number may be
      // from before, so it is not shown; the read-all's own reload follows.
      if (startedDuringReadAll || readAllPending.current > 0) return;
      setNotifications(data.notifications || []);
      setUnreadCount(data.counts ? data.counts.unread : (data.unreadCount || 0));
      setNotificationCounts(data.counts || null);
      setNotificationsLoadFailed(false);
    } catch (err) {
      console.error('Error loading notifications:', err);
      setNotificationsLoadFailed(true);
    }
  }, []);

  // The two numbers alone (GET /api/notifications/counts), after something changes them without
  // changing the list: a suggestion accepted or dismissed (aidey:tasksUpdated), a dismissal here.
  // A failed refresh keeps the last numbers the server gave rather than inventing new ones.
  const refreshNotificationCounts = useCallback(async () => {
    if (!getAccessToken()) return;
    try {
      const res = await api('/api/notifications/counts');
      const counts = await res.json();
      if (readAllPending.current > 0) return;   // as loadNotifications: never the pre-read-all number
      if (counts && typeof counts.needsAttention === 'number' && typeof counts.unread === 'number') {
        setNotificationCounts(counts);
        setUnreadCount(counts.unread);
      }
    } catch (err) {
      console.error('Error loading notification counts:', err);
    }
  }, []);
  useEffect(() => {
    const onChange = () => { refreshNotificationCounts(); };
    window.addEventListener('aidey:tasksUpdated', onChange);
    return () => window.removeEventListener('aidey:tasksUpdated', onChange);
  }, [refreshNotificationCounts]);

  // Notifications are now workspace-scoped server-side (by the X-Active-Workspace
  // header), so refetch when the active workspace changes — otherwise the bell/
  // dashboard would show the previous workspace's set until the next reload.
  useEffect(() => {
    if (activeWorkspaceId) loadNotifications();
  }, [activeWorkspaceId, loadNotifications]);

  const switchWorkspace = useCallback((id) => {
    setActiveWorkspaceId(id);
    setWorkspaceHeader(id);
    try { localStorage.setItem(activeWorkspaceKey(), id); } catch (_) {}
  }, [activeWorkspaceKey]);

  const loadWorkspacesInner = useCallback(async () => {
    const tryFetch = async () => {
      const res = await api('/api/workspaces');
      return res.json();
    };
    let list;
    try {
      list = await tryFetch();
    } catch {
      try {
        list = await tryFetch();
      } catch (err) {
        console.error('[workspaces] failed to load after retry:', err);
        setWorkspacesLoadError(true);
        const fallback = workspacesRef.current.find(w => w.type === 'business') || workspacesRef.current[0];
        if (fallback) {
          setActiveWorkspaceId(fallback.id);
          setWorkspaceHeader(fallback.id);
        }
        return;
      }
    }
    setWorkspacesLoadError(false);
    setWorkspaces(list);
    workspacesRef.current = list;

    // Restore the last active workspace if it is still in the fetched list.
    // Priority: saved preference → the server's default → owned business → member
    // workspace → list[0]. Member workspaces are flagged isMember:true by the backend;
    // suspended members are excluded from the list at the API level, so no guard needed here.
    // isDefault (TEAM-1a, decision 25) is where the server itself lands this person: for a
    // team member, the business's SHARED workspace. Picking "the first business workspace
    // they own" instead put every member who had signed up in their own empty one.
    let restoredId = null;
    try {
      const saved = readSavedWorkspace();
      if (saved && list.find(w => w.id === saved)) restoredId = saved;
    } catch (_) {}

    const restored  = restoredId ? list.find(w => w.id === restoredId) : null;
    const landing   = list.find(w => w.isDefault);
    const biz       = list.find(w => w.type === 'business' && !w.isMember);
    const memberWs  = list.find(w => w.isMember);
    const target    = restored || landing || biz || memberWs || list[0];
    if (target) {
      setActiveWorkspaceId(target.id);
      setWorkspaceHeader(target.id);
    }
  }, []);

  const loadWorkspaces = useCallback(async () => {
    if (!getAccessToken()) return;
    setWorkspacesSettled(false);
    try { await loadWorkspacesInner(); } finally { setWorkspacesSettled(true); }
  }, [loadWorkspacesInner]);


  const markNotificationAsRead = useCallback(async (notificationId) => {
    if (!currentUser) return;

    try {
      await api(`/api/notifications/${notificationId}/read`, { method: 'PUT' });
      await loadNotifications();
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  }, [currentUser, loadNotifications]);

  // Optimistic, then confirmed (Carla, 2026-10-21): every bell, the page and the tile read these, so
  // they all show 0 unread the instant she asks; the server's answer then replaces the guess. If the
  // server refuses, everything is put back and she is told why, never left believing it worked.
  const markAllNotificationsAsRead = useCallback(async () => {
    if (!currentUser) return;
    const before = { list: notificationsRef.current, counts: notificationCountsRef.current };
    const next = markAllReadLocally(before.list, before.counts);
    setNotifications(next.notifications);
    setNotificationCounts(next.counts);
    setUnreadCount(0);
    readAllPending.current += 1;
    try {
      await api('/api/notifications/read-all', { method: 'PUT' });
    } catch (err) {
      readAllPending.current -= 1;
      setNotifications(before.list);
      setNotificationCounts(before.counts);
      setUnreadCount(before.counts ? before.counts.unread : 0);
      showErrorToast(messageFromError(err, 'Your notifications could not be marked as read. Try again.'));
      return;
    }
    readAllPending.current -= 1;
    await loadNotifications();
  }, [currentUser, loadNotifications]);

  // The bell's "Clear all" (Carla, 2026-10-21): dismiss the READ ones she was shown under "Earlier".
  // The server dismisses only read ones among the ids (PUT /api/notifications/clear-read). Throws on
  // failure so the caller's Undo puts them back and says the server's words.
  const clearReadNotifications = useCallback(async (ids, { keepalive = false } = {}) => {
    if (!ids?.length) return;
    await api('/api/notifications/clear-read', { method: 'PUT', body: JSON.stringify({ ids }), keepalive });
    const idSet = new Set(ids);
    setNotifications((prev) => prev.filter((n) => !(idSet.has(n.id) && n.read)));
    refreshNotificationCounts();
  }, [refreshNotificationCounts]);

  const dismissNotification = useCallback(async (id) => {
    try {
      await api(`/api/notifications/${id}/dismiss`, { method: 'PUT' });
      setNotifications(prev => prev.filter(n => n.id !== id));
      // The server's numbers, not a local "minus one" (a read one was never in unread).
      refreshNotificationCounts();
    } catch (err) {
      console.error('Error dismissing notification:', err);
    }
  }, [refreshNotificationCounts]);

  const dismissNotifications = useCallback(async (ids) => {
    if (!ids?.length) return;
    try {
      await api('/api/notifications/dismiss-batch', {
        method: 'PUT',
        body: JSON.stringify({ ids }),
      });
      const idSet = new Set(ids);
      setNotifications(prev => prev.filter(n => !idSet.has(n.id)));
      refreshNotificationCounts();
    } catch (err) {
      console.error('Error dismissing notifications:', err);
    }
  }, [refreshNotificationCounts]);

  // @mention notification. The old path wrote to an in-memory mock and never
  // reached the backend, so mentions produce no real notification today. Kept as
  // a no-op to preserve that contract (TaskForm still calls it).
  // TODO: real mention notifications via POST /api/notifications.
  const notifyUserMentioned = useCallback(() => {}, []);

  // ✅ SESSION RESTORATION
  const restoreSession = useCallback(async () => {
    try {
      setLoading(true);
      setAuthConnectionFailed(false);

      // refreshAccessToken() participates in the shared refreshPromise so any
      // concurrent api() 401-retry waits on the same fetch rather than firing
      // a second rotation request that would invalidate the first.
      const data = await refreshAccessToken();

      if (data === 'network-error') {
        // A fetch failure (DNS, timeout, network down) — leave currentUser untouched
        // so an in-memory session (e.g. bfcache restore) survives, and show a
        // retry card instead of the login page.
        setAuthConnectionFailed(true);
        return;
      }

      if (data && data.user) {
        // New backend response includes user — no second round-trip needed.
        setCurrentUser(data.user);
        registerActiveAccount(data.user);
        applyLanguage(data.user?.language);
        try {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (tz && tz !== data.user?.timezone) {
            api('/api/users/me/timezone', { method: 'PATCH', body: JSON.stringify({ timezone: tz }) }).catch(() => {});
          }
        } catch (_) {}
        // Carla, 2026-10-11 (the 8 second "Loading…"): notifications are no longer awaited here.
        // The effect on activeWorkspaceId loads them once the workspace is known; fetched first, they
        // held the splash for a request that was thrown away and fetched again.
        await loadWorkspaces();
      } else if (data && data.accessToken) {
        // Older backend compat: token refreshed but no user in response — fetch separately.
        const meRes = await api('/api/auth/me');
        if (meRes && meRes.ok) {
          const meData = await meRes.json();
          setCurrentUser(meData.user);
          registerActiveAccount(meData.user);
          applyLanguage(meData.user?.language);
          try {
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (tz && tz !== meData.user?.timezone) {
              api('/api/users/me/timezone', { method: 'PATCH', body: JSON.stringify({ timezone: tz }) }).catch(() => {});
            }
          } catch (_) {}
          // Carla, 2026-10-11 (the 8 second "Loading…"): notifications are no longer awaited here.
          // The effect on activeWorkspaceId loads them once the workspace is known; fetched first, they
          // held the splash for a request that was thrown away and fetched again.
          await loadWorkspaces();
        }
      }
      // data === false → refresh token invalid/missing → stay logged out
    } catch (err) {
      console.error('Error restoring session:', err);
    } finally {
      setLoading(false);
    }
  }, [loadNotifications]);

  const retryConnection = useCallback(() => {
    restoreSession();
  }, [restoreSession]);

  // ── Account switching (Block 194) ───────────────────────────────────────────
  // Switch the active account to another slot the browser already holds. Mints
  // that slot's access token (X-Account-Slot drives which cookie /refresh reads),
  // then reloads the CurrentUser context fully for the new identity. Reverts if
  // the target session is dead, so a stale entry never strands the user.
  const switchAccount = useCallback(async (slot) => {
    const prev = getActiveSlot();
    if (slot === prev) return { ok: true };
    setLoading(true);
    setActiveSlot(slot);
    const data = await refreshAccessToken();
    if (!data || data === 'network-error' || !data.user) {
      setActiveSlot(prev); // revert, the previous session is untouched in memory
      setLoading(false);
      setAccounts(getAccounts());
      return { ok: false, reason: 'session_expired' };
    }
    setActiveWorkspaceId(null);
    setWorkspaceHeader(null);
    setCurrentUser(data.user);
    registerActiveAccount(data.user);
    applyLanguage(data.user?.language);
    // Carla, 2026-10-11 (the 8 second "Loading…"): notifications are no longer awaited here.
    // The effect on activeWorkspaceId loads them once the workspace is known; fetched first, they
    // held the splash for a request that was thrown away and fetched again.
    await loadWorkspaces();
    setAccounts(getAccounts());
    setLoading(false);
    return { ok: true };
  }, [loadNotifications, loadWorkspaces, registerActiveAccount]);

  // Sign in to a SECOND account (an existing one) in a free slot, WITHOUT
  // disturbing the current slot. Uses a raw fetch with a per-request slot header
  // so the api() active-slot 401-retry can't refresh (or expire) the other slot.
  const addAccount = useCallback(async (email, password) => {
    const slot = nextFreeSlot();
    if (slot < 0) throw new Error(`You can hold at most ${MAX_ACCOUNT_SLOTS} accounts in one browser.`);
    const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';
    const res = await fetch(`${BASE}/api/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-Account-Slot': String(slot) },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Sign in failed');
    upsertAccount({ slot, userId: data.user.id, label: data.user.name || data.user.email });
    setActiveSlot(slot);
    setActiveWorkspaceId(null);
    setWorkspaceHeader(null);
    setAccessToken(data.accessToken); // writes this slot's aidey_at_<slot>
    setCurrentUser(data.user);
    registerActiveAccount(data.user);
    applyLanguage(data.user?.language);
    // Carla, 2026-10-11 (the 8 second "Loading…"): notifications are no longer awaited here.
    // The effect on activeWorkspaceId loads them once the workspace is known; fetched first, they
    // held the splash for a request that was thrown away and fetched again.
    await loadWorkspaces();
    setAccounts(getAccounts());
    return data.user;
  }, [loadNotifications, loadWorkspaces, registerActiveAccount]);

  // Remove an account from this browser: revoke its session server-side (Block
  // 193 /logout is slot-aware) and drop its slot locally. If it was active,
  // switch to a remaining account or fall back to the logged-out state.
  const removeAccount = useCallback(async (slot) => {
    try {
      const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      await fetch(`${BASE}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Account-Slot': String(slot) },
      });
    } catch (_) { /* best-effort; local drop still proceeds */ }
    const wasActive = slot === getActiveSlot();
    removeAccountEntry(slot);
    setAccounts(getAccounts());
    if (wasActive) {
      const remaining = getAccounts()[0];
      if (remaining) {
        await switchAccount(remaining.slot);
      } else {
        setActiveSlot(0);
        setAccessToken(null);
        setCurrentUser(null);
        setNotifications([]);
        setUnreadCount(0);
        setNotificationCounts(null);
      }
    }
  }, [switchAccount]);

  const updateCurrentUser = useCallback((updates) => {
    return updateProfile(updates);
  }, [updateProfile]);

  // ✅ PERMISSIONS
  const hasPermission = useCallback((permission) => {
    if (!currentUser) return false;

    const permissions = {
      admin: ['admin', 'super_admin'].includes(currentUser.role),
      super_admin: currentUser.role === 'super_admin',
      create_team: ['member', 'admin', 'super_admin'].includes(currentUser.role),
      manage_billing: ['admin', 'super_admin'].includes(currentUser.role),
      view_analytics: ['admin', 'super_admin'].includes(currentUser.role)
    };

    return permissions[permission] || false;
  }, [currentUser]);

  // ✅ EFFECT TO RESTORE SESSION ON MOUNT
  useEffect(() => {
    restoreSession();
  }, [restoreSession]);

  // ✅ CLEAR AUTH WHEN API CLIENT SIGNALS SESSION IS DEAD
  useEffect(() => {
    const handleAuthExpired = () => {
      // SAY WHY, BEFORE THE SCREEN THAT COULD SAY IT IS UNMOUNTED (2026-10-04).
      // Everything below wipes the authenticated tree, so a message put in React state
      // here would be destroyed by the same handler that set it. noteSessionEnded writes
      // to sessionStorage and the login screen reads it once.
      noteSessionEnded('refresh-failed');
      setAccessToken(null);
      setCurrentUser(null);
      setNotifications([]);
      setUnreadCount(0);
      setNotificationCounts(null);
      localStorage.removeItem('currentUserId');
      localStorage.removeItem('sessionToken');
      localStorage.removeItem('currentUser');
    };
    window.addEventListener('aidey:auth:expired', handleAuthExpired);
    return () => window.removeEventListener('aidey:auth:expired', handleAuthExpired);
  }, []);

  useEffect(() => {
    let retryTimer1 = null;
    let retryTimer2 = null;

    async function checkPersonalWorkspace() {
      const token = getAccessToken();
      if (!token) return false;
      try {
        const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';
        const res = await fetch(`${BASE}/api/notes`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
        if (res.ok) return true;
        const body = await res.json().catch(() => ({}));
        return body?.code !== 'PERSONAL_SUBSCRIPTION_REQUIRED';
      } catch {
        return false;
      }
    }

    const handleSubscriptionRequired = () => {
      if (typeof window === 'undefined' || window.location.pathname === '/plan-select') return;
      setSubscriptionCheckPending(true);

      retryTimer1 = setTimeout(async () => {
        const ok = await checkPersonalWorkspace();
        if (ok) { setSubscriptionCheckPending(false); return; }

        retryTimer2 = setTimeout(async () => {
          const ok2 = await checkPersonalWorkspace();
          setSubscriptionCheckPending(false);
          if (!ok2) window.location.href = '/plan-select';
        }, 7000);
      }, 3000);
    };

    window.addEventListener('aidey:subscription:required', handleSubscriptionRequired);
    return () => {
      window.removeEventListener('aidey:subscription:required', handleSubscriptionRequired);
      clearTimeout(retryTimer1);
      clearTimeout(retryTimer2);
    };
  }, []);

  // ACCOUNT-4 — access enforcement. When the API 402s a blocked user, pull a
  // fresh currentUser (via the exempt /refresh path) so its enforcing verdict
  // (trialExpired/blockReason) is current and App renders the paywall. Guarded
  // so a burst of 402s from many in-flight calls triggers at most one refresh.
  useEffect(() => {
    let refreshing = false;
    const handleAccessBlocked = async () => {
      if (refreshing) return;
      if (currentUser?.trialExpired) return; // already reflecting the block
      refreshing = true;
      try { await restoreSession(); } finally { refreshing = false; }
    };
    window.addEventListener('aidey:access:blocked', handleAccessBlocked);
    return () => window.removeEventListener('aidey:access:blocked', handleAccessBlocked);
  }, [currentUser, restoreSession]);

  // Card at sign-up (phase 2): the API answers 402 CARD_SETUP_REQUIRED to an account that has not
  // finished starting its trial. Pull a fresh currentUser once so cardSetupRequired is current and
  // App shows the "Finish starting your free trial" screen. Guarded like the block above.
  useEffect(() => {
    let refreshing = false;
    const handleCardRequired = async () => {
      if (refreshing || currentUser?.cardSetupRequired) return;
      refreshing = true;
      try { await restoreSession(); } finally { refreshing = false; }
    };
    window.addEventListener('aidey:card:required', handleCardRequired);
    return () => window.removeEventListener('aidey:card:required', handleCardRequired);
  }, [currentUser, restoreSession]);

  // ✅ WINDOW FOCUS HEALTH CHECK — proactively refresh if token is close to expiry.
  // Uses the shared refreshAccessToken() from the api client so it participates in
  // the refreshPromise deduplication — prevents a race where a background poll and
  // this handler both try to rotate the refresh token simultaneously, causing one
  // to receive a 401 from an already-consumed token and incorrectly firing logout.
  useEffect(() => {
    if (!currentUser) return;
    const handleFocus = async () => {
      const token = getAccessToken();
      if (!token) return;
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expiresAt = payload.exp * 1000;
        if (expiresAt - Date.now() < 5 * 60 * 1000) {
          // Token expires in less than 5 minutes — refresh proactively.
          // refreshAccessToken() is safe to call concurrently: if a refresh is
          // already in flight it returns the same promise rather than a new fetch.
          await refreshAccessToken();
        }
      } catch (_) {}
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [currentUser]);

  // ✅ EFFECT TO POLL FOR NEW NOTIFICATIONS
  useEffect(() => {
    if (!currentUser) return;

    const pollInterval = setInterval(() => {
      loadNotifications();
    }, 30000); // Poll every 30 seconds

    return () => clearInterval(pollInterval);
  }, [currentUser, loadNotifications]);

  // ✅ CONTEXT VALUE
  const value = {
    // User state
    currentUser,
    loading,
    error,
    notifications,
    notificationsLoadFailed,
    unreadCount,
    notificationCounts,
    refreshNotificationCounts,

    // Authentication
    login,
    logout,
    register,

    // Plan & billing (card at sign-up, phase 3): re-read the account after a plan change or a
    // paid bill, through the same exempt /refresh path the 402 handler uses.
    refreshCurrentUser: restoreSession,

    // Profile management
    updateProfile,
    changePassword,

    // Notifications
    markNotificationAsRead,
    markAllNotificationsAsRead,
    loadNotifications,
    dismissNotification,
    dismissNotifications,
    clearReadNotifications,

    // @Mentions (suggestion/parsing moved to useWorkspaceRoster)
    notifyUserMentioned,

    // Permissions
    hasPermission,

    // Workspace
    workspaces,
    activeWorkspaceId,
    switchWorkspace,
    loadWorkspaces,
    workspacesLoadError,
    workspacesSettled,

    // Multi-account (Block 194)
    accounts,
    activeSlot: getActiveSlot(),
    multiAccountEnabled,
    switchAccount,
    addAccount,
    removeAccount,
    refreshAccounts,

    // Utilities
    restoreSession,
    authConnectionFailed,
    retryConnection,
    subscriptionCheckPending,

    // Legacy compatibility
    setCurrentUser,
    updateCurrentUser,
    isAdmin:
      currentUser?.role === 'admin'
      || currentUser?.role === 'super_admin'
      || ((currentUser?.plan === 'Team' || currentUser?.plan === 'Enterprise') && !!currentUser?.isTeamOwner),

    // True for global admins, team admins, AND solo owners (no team) so a
    // one-person business can reach its own billing/financials. Only a non-admin
    // INVITED team member (has a teamId but teamRole 'user') gets false.
    isTeamAdmin:
      currentUser?.role === 'admin'
      || currentUser?.role === 'super_admin'
      || currentUser?.teamRole === 'admin'
      || (!!currentUser && !currentUser?.teamId),

    // Plan helpers (use these instead of checking plan strings directly)
    isPro: isPro(currentUser),
    isTeam: isTeam(currentUser),
  };

  return (
    <CurrentUserContext.Provider value={value}>
      {children}
    </CurrentUserContext.Provider>
  );
};

export { CurrentUserContext };
export default CurrentUserProvider;
